import { useEffect, useState } from 'react';

import {
  BackrsPopover,
  HideTourExpandableButton,
  CareerCategory,
  Tier,
  BackrsUser,
  SIDEBAR_COLLAPSED_WIDTH,
} from '@backrs_ui/common-ui';

import { appStrings } from '@backrs_ui/grow-ui/src/assets/appStrings';
import { transformSchoolLevel } from '@backrs_ui/grow-ui/src/components/MarkersOfSuccess/markersOfSuccess.util';
import {
  CareerPathWithScore,
  CareerQuestion,
  CareerUserResponseSet,
} from '@backrs_ui/grow-ui/src/models/models';
import { Tour } from '@backrs_ui/grow-ui/src/redux/tour/tour';

import { Box, SxProps, Theme } from '@mui/material';
import introJs from 'intro.js';
import { usePostHog } from 'posthog-js/react';

import { ResultPageHeader } from './atoms/ResultPageHeader';
import { CareerPathDialogForTour } from './molecules/CareerPathDialogForTour';
import { CareerPaths } from './molecules/CareerPaths';
import { MarkersOfSuccess } from './molecules/MarkersOfSuccess';
import { TrackExplanation } from './molecules/TrackExplanation';
import { startResultPageTour } from './tour/ResultPage.tour';

type ResultPageProps = Readonly<{
  tier: Tier;
  entrepreneurshipRecommended: boolean;
  userResponseSet: CareerUserResponseSet;
  careerQuestions: CareerQuestion[];
  careerPathSuggestions: CareerPathWithScore[];
  careerPathCategories: CareerCategory[];
  firstName?: string;
  lastName?: string;
  createdAt: string;
  user?: BackrsUser;
  resultsPageTour?: Tour;
  onFinishOrSkipTour: () => void;
}>;

export const ResultPage = ({
  tier,
  entrepreneurshipRecommended,
  userResponseSet,
  careerQuestions,
  careerPathSuggestions,
  careerPathCategories,
  firstName,
  lastName,
  createdAt,
  user,
  resultsPageTour,
  onFinishOrSkipTour,
}: ResultPageProps) => {
  const postHog = usePostHog();

  const [expandedMarkersOfSuccessIndex, setExpandedMarkersOfSuccessIndex] = useState<number | null>(
    null,
  );

  const [isTourActive, setIsTourActive] = useState(false);
  const [isTourCareerPathDialogOpen, setIsTourCareerPathDialogOpen] = useState(false);

  const [isReturnToTourPopoverOpen, setIsReturnToTourPopoverOpen] = useState(false);

  const handleFinishOrSkipTour = () => {
    onFinishOrSkipTour();
    setIsTourActive(false);
    handleShowingPopover();
    document.body.style.overflow = '';
  };

  const handleShowingPopover = () => {
    setIsReturnToTourPopoverOpen(true);

    setTimeout(() => {
      setIsReturnToTourPopoverOpen(false);
    }, 5000);
  };

  const startTour = (start_type: 'FIRST_TIME' | 'RESTART') => {
    startResultPageTour({
      setExpandedMarkersOfSuccessIndex,
      onFinishOrSkipTour: handleFinishOrSkipTour,
      setIsCareerPathDialogOpen: setIsTourCareerPathDialogOpen,
      onStartOfTour: () => setIsTourActive(true),
      postHog,
      start_type,
      user_id: user?.id,
    });
  };

  useEffect(() => {
    const timer = setTimeout(() => {
      if (resultsPageTour && !resultsPageTour.has_user_completed) {
        document.body.style.overflow = 'hidden'; // Prevent scrolling during the tour
        startTour('FIRST_TIME'); // Start the tour on initial load
      } else {
        // IntroJS sets overflow to hidden, so we need to reset it
        document.body.style.overflow = '';
        setIsTourActive(false);
      }
    }, 500);

    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [resultsPageTour]);

  return (
    <Box sx={containerSx}>
      <CareerPathDialogForTour
        open={isTourCareerPathDialogOpen}
        onClose={() => setIsTourCareerPathDialogOpen(false)}
        careerPathSuggestions={careerPathSuggestions}
        tier={tier}
      />
      {resultsPageTour && (
        <HideTourExpandableButton
          isTourActive={isTourActive}
          onShowTourClick={() => {
            startTour('RESTART');
          }}
          onHideTourClick={() => {
            setIsTourActive(false);
            handleFinishOrSkipTour();
            introJs().exit(true);
            document.body.style.overflow = '';
          }}
        />
      )}
      <BackrsPopover
        anchorId="hide-tour-expandable-button"
        isPopoverOpen={isReturnToTourPopoverOpen}
        setIsPopoverOpen={setIsReturnToTourPopoverOpen}
        children={<>{appStrings.tours.restartTourLater}</>}
      />
      <Box sx={contentContainerSx}>
        <ResultPageHeader
          tier={tier}
          entrepreneurshipRecommended={entrepreneurshipRecommended}
          userResponseSet={userResponseSet}
          careerPathSuggestions={careerPathSuggestions}
          careerQuestions={careerQuestions}
          careerPathCategories={careerPathCategories}
          firstName={firstName}
          lastName={lastName}
          createdAt={createdAt}
        />
        <TrackExplanation
          minimumTrack={tier}
          userResponseSet={userResponseSet}
          careerQuestions={careerQuestions}
          entrepreneurshipRecommended={entrepreneurshipRecommended}
        />
        <CareerPaths
          careerPathSuggestions={careerPathSuggestions}
          careerPathCategories={careerPathCategories}
          userResponses={userResponseSet.user_responses}
        />
        {userResponseSet.school_level && (
          <MarkersOfSuccess
            schoolLevel={transformSchoolLevel(userResponseSet.school_level)}
            tier={tier}
            expandedMarkersOfSuccessIndex={expandedMarkersOfSuccessIndex}
          />
        )}
      </Box>
    </Box>
  );
};

const containerSx: SxProps<Theme> = {
  height: '100svh',
  overflowY: 'scroll',
  maxWidth: '1512px',
  margin: '0 auto',
  alignSelf: 'center',
  marginLeft: `${SIDEBAR_COLLAPSED_WIDTH}px`,
} as const;

const contentContainerSx: SxProps<Theme> = {
  ml: {
    xs: `${9.5 * 3}px`,
    sm: `${14.25 * 4}px`,
  },
  mr: {
    xs: `${28 * 0.75}px`,
    sm: `${28 * 2}px`,
  },
} as const;
